import firebase from 'firebase/app'
import 'firebase/auth'
import firebasePromise from './firebase'
import { updateAccessToken } from './apiToken'

// Resolve with the current Firebase user, waiting for the first onAuthStateChanged
// emission so persistence-rehydration after a page refresh has a chance to populate
// auth state before we judge "signed in or not".
const waitForFirebaseUser = async () => {
    const fb = await firebasePromise;
    return new Promise((resolve, reject) => {
        const unsubscribe = fb.auth().onAuthStateChanged(u => {
            unsubscribe();
            resolve(u || fb.auth().currentUser || null);
        }, reject);
    });
};

const authProvider =  {
    login: async ({user }) => {
        const idToken = await user.getIdToken(false);
        localStorage.setItem("idtoken", idToken);
        // Exchange the Firebase id token for a betmarket access token so that
        // localStorage.permissions is populated with this user's actual
        // authorities (admin / superadmin) for usePermissions() in the UI.
        try {
            await updateAccessToken();
        } catch (e) {
            // Failure here means the user is signed in to Firebase but the
            // backend rejected the token exchange. Let login proceed — API
            // calls will surface the 401/403 with a clearer error.
            console.warn("authProvider: updateAccessToken failed", e);
            localStorage.setItem("permissions", JSON.stringify([]));
        }
    },
    logout: () => {
        firebase.auth().signOut();
        localStorage.removeItem('token');
        localStorage.removeItem('idtoken');
        localStorage.removeItem('permissions');
        localStorage.removeItem('signedin');
        return Promise.resolve();
    },
    checkError: ({ status }) => {
        // 401/403 from the API → token is expired or the user no longer has
        // admin authority. Reject so react-admin redirects to the login page
        // instead of failing silently as a "data didn't load" error.
        if (status === 401 || status === 403) {
            return Promise.reject();
        }
        return Promise.resolve();
    },
    checkAuth: async () => {
        // Verify Firebase actually has a signed-in user, not just that localStorage
        // has a stale idtoken from a previous session. Otherwise the first API call
        // will trigger fetchAccessToken which throws "no Firebase user signed in".
        const user = await waitForFirebaseUser();
        if (!user) {
            // Clear stale local state so we don't loop back into this state next load.
            localStorage.removeItem('idtoken');
            localStorage.removeItem('token');
            localStorage.removeItem('permissions');
            return Promise.reject();
        }
        return Promise.resolve();
    },
    getPermissions: () => {
        const raw = localStorage.getItem('permissions');
        if (!raw) return Promise.resolve([]);
        try {
            const parsed = JSON.parse(raw);
            return Promise.resolve(Array.isArray(parsed) ? parsed : []);
        } catch (e) {
            return Promise.resolve([]);
        }
    }
};

export default authProvider;
